import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, CheckCircle, XCircle, Target, ChevronLeft, ChevronRight } from "lucide-react";

export function AssessmentsTab({
  assessments,
  assessmentSummary,
  assessmentPagination,
  setAssessmentPagination,
  formatDate
}) {
  return (
    <div className="space-y-4">
      {/* Summary Stats - Responsive 2x2 on mobile, 4-col on desktop */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-2.5 px-3 sm:py-3 sm:px-4 text-center">
            <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 mx-auto text-blue-600 mb-1" />
            <p className="text-lg sm:text-xl font-bold text-blue-700">{assessmentSummary?.total_questions || 0}</p>
            <p className="text-[10px] sm:text-xs text-blue-600 font-medium truncate">Questions</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="py-2.5 px-3 sm:py-3 sm:px-4 text-center">
            <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 mx-auto text-green-600 mb-1" />
            <p className="text-lg sm:text-xl font-bold text-green-700">{assessmentSummary?.correct_answers || 0}</p>
            <p className="text-[10px] sm:text-xs text-green-600 font-medium truncate">Correct</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="py-2.5 px-3 sm:py-3 sm:px-4 text-center">
            <XCircle className="h-4 w-4 sm:h-5 sm:w-5 mx-auto text-red-600 mb-1" />
            <p className="text-lg sm:text-xl font-bold text-red-700">{assessmentSummary?.incorrect_answers || 0}</p>
            <p className="text-[10px] sm:text-xs text-red-600 font-medium truncate">Incorrect</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="py-2.5 px-3 sm:py-3 sm:px-4 text-center">
            <Target className="h-4 w-4 sm:h-5 sm:w-5 mx-auto text-purple-600 mb-1" />
            <p className="text-lg sm:text-xl font-bold text-purple-700">{assessmentSummary?.accuracy_percent || 0}%</p>
            <p className="text-[10px] sm:text-xs text-purple-600 font-medium truncate">Accuracy</p>
          </CardContent>
        </Card>
      </div>

      {/* Assessment History - Compact */}
      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-base">Assessment History</CardTitle>
          <CardDescription className="text-xs">Review past questions and answers</CardDescription>
        </CardHeader>
        <CardContent className="pt-0 pb-3 px-4">
          {assessments.length === 0 ? (
            <p className="text-center text-gray-500 py-6 text-sm">No assessments yet. Start learning on WhatsApp!</p>
          ) : (
            <>
              <div className="space-y-2">
                {assessments.map((a) => (
                  <div key={a.id || a._id || `${a.question}-${a.created_at}`} className={`border rounded-md p-3 ${a.is_correct ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {a.is_correct ? (
                            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                          )}
                          <span className={`text-xs font-medium ${a.is_correct ? 'text-green-700' : 'text-red-700'}`}>
                            {a.is_correct ? 'Correct' : 'Incorrect'}
                          </span>
                          {a.subject && (
                            <span className="px-1.5 py-0.5 bg-gray-200 text-gray-700 text-[10px] rounded-full">{a.subject}</span>
                          )}
                        </div>
                        <p className="text-gray-900 text-sm font-medium truncate">{a.question}</p>
                        <div className="text-xs mt-1">
                          <span className="text-gray-500">Answer: </span>
                          <span className={a.is_correct ? 'text-green-700' : 'text-red-700'}>{a.student_answer}</span>
                          {!a.is_correct && a.correct_answer && (
                            <span className="ml-2 text-green-700">(Correct: {a.correct_answer})</span>
                          )}
                        </div>
                      </div>
                      <span className="text-[10px] text-gray-500 whitespace-nowrap">{formatDate(a.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination - Compact */}
              {assessmentPagination.total > assessmentPagination.limit && (
                <div className="flex items-center justify-between mt-4 pt-3 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setAssessmentPagination(p => ({ ...p, skip: Math.max(0, p.skip - p.limit) }))}
                    disabled={assessmentPagination.skip === 0}
                  >
                    <ChevronLeft className="h-3 w-3 mr-1" /> Prev
                  </Button>
                  <span className="text-xs text-gray-500">
                    {assessmentPagination.skip + 1}-{Math.min(assessmentPagination.skip + assessmentPagination.limit, assessmentPagination.total)} of {assessmentPagination.total}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setAssessmentPagination(p => ({ ...p, skip: p.skip + p.limit }))}
                    disabled={assessmentPagination.skip + assessmentPagination.limit >= assessmentPagination.total}
                  >
                    Next <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
